/**
 * ScheduleWidget — Виджет расписания занятий на странице организации
 *
 * Отображает таблицу занятий на выбранную неделю, сгруппированную по дням.
 * Навигация по неделям (← →). При клике на строку — модалка редактирования.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Stack, Group, Text, Badge, Table, Center, Loader, ActionIcon, Box, Tooltip
} from '@mantine/core';
import {
  IconCalendarTime, IconChevronLeft, IconChevronRight, IconPoint,
  IconRefresh
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import BaseWidget from './BaseWidget';
import LessonEditModal from './LessonEditModal';
import { getWeekSchedule, generateLessons } from '../../../services/api/lessonsService';
import tokens from '../../../theme/designTokens';
import { useSession } from '../../../context/SessionContext';

// Метки статусов
const STATUS_CONFIG = {
  active: { label: 'В силе', color: 'green' },
  cancelled: { label: 'Отмена', color: 'red' },
  substitute: { label: 'Замена', color: 'orange' },
  rescheduled: { label: 'Перенос', color: 'blue' },
  was_rescheduled: { label: 'Из переноса', color: 'cyan' },
};

// Метки типов
const TYPE_CONFIG = {
  lesson: { label: 'ЗН', color: 'gray' },
  master_class: { label: 'МК', color: 'violet' },
  offline: { label: 'offline', color: 'teal' },
  online: { label: 'online', color: 'indigo' },
  group_online: { label: 'гр. онлайн', color: 'grape' },
};

function getMonday(d = new Date()) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  return date.toISOString().split('T')[0];
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Moscow',
  });
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  return timeStr.slice(0, 5); // "HH:MM:SS" → "HH:MM"
}

const ScheduleWidget = ({ organization, onActivityChange }) => {
  const { user } = useSession();
  const [weekStart, setWeekStart] = useState(getMonday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editLesson, setEditLesson] = useState(null);

  const canEdit = ['ADMIN', 'MANAGER'].includes(user?.role);

  const loadSchedule = useCallback(async () => {
    if (!organization?.id) return;
    try {
      setLoading(true);
      const result = await getWeekSchedule(organization.id, weekStart);
      setData(result);
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  }, [organization?.id, weekStart]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const prevWeek = () => {
    const d = new Date(weekStart + 'T00:00:00');
    d.setDate(d.getDate() - 7);
    setWeekStart(d.toISOString().split('T')[0]);
  };
  const nextWeek = () => {
    const d = new Date(weekStart + 'T00:00:00');
    d.setDate(d.getDate() + 7);
    setWeekStart(d.toISOString().split('T')[0]);
  };
  const goToCurrentWeek = () => {
    setWeekStart(getMonday());
  };

  const handleGenerate = async () => {
    try {
      const result = await generateLessons(organization.id);
      notifications.show({
        title: 'Генерация',
        message: `Создано ${result.generated_count} занятий`,
        color: 'green',
      });
      await loadSchedule();
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    }
  };

  const handleLessonSaved = async () => {
    setEditLesson(null);
    await loadSchedule();
    if (onActivityChange) onActivityChange();
  };

  const totalLessons = data?.summary?.total || 0;

  // Кнопки навигации по неделям
  const weekNav = (
    <Group gap={4}>
      <Tooltip label="Предыдущая неделя">
        <ActionIcon variant="subtle" size="sm" onClick={prevWeek}>
          <IconChevronLeft size={16} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Текущая неделя">
        <ActionIcon variant="subtle" size="sm" onClick={goToCurrentWeek}>
          <IconPoint size={16} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Следующая неделя">
        <ActionIcon variant="subtle" size="sm" onClick={nextWeek}>
          <IconChevronRight size={16} />
        </ActionIcon>
      </Tooltip>
      {canEdit && (
        <Tooltip label="Сгенерировать занятия на 4 недели">
          <ActionIcon variant="subtle" size="sm" onClick={handleGenerate}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );

  return (
    <>
      <BaseWidget
        title="Расписание занятий"
        icon={IconCalendarTime}
        count={totalLessons}
        actions={weekNav}
        defaultExpanded={true}
        collapsible={true}
      >
        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : data && data.days ? (
          <Stack gap="md">
            {/* Период */}
            <Text size="xs" c="dimmed" ta="center">
              {formatDate(data.week_start)} — {formatDate(data.week_end)}
            </Text>

            {data.days.map((day) => {
              if (day.lessons.length === 0) return null;
              return (
                <Box key={day.date}>
                  {/* Заголовок дня */}
                  <Text fw={600} size="sm" mb="xs" c={tokens.colors.neutral[8]}>
                    {day.day_name}, {formatDate(day.date)}
                  </Text>

                  <Table
                    striped
                    highlightOnHover
                    withTableBorder={false}
                    style={{ fontSize: tokens.typography.fontSize[13] }}
                  >
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Группа</Table.Th>
                        <Table.Th>Время</Table.Th>
                        <Table.Th>Тип</Table.Th>
                        <Table.Th>Статус</Table.Th>
                        <Table.Th>Замена</Table.Th>
                        <Table.Th>Педагог</Table.Th>
                        <Table.Th>Предмет</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {day.lessons.map((lesson) => {
                        const statusCfg = STATUS_CONFIG[lesson.status] || STATUS_CONFIG.active;
                        const typeCfg = TYPE_CONFIG[lesson.lesson_type] || TYPE_CONFIG.lesson;
                        const substituteText = lesson.substitute_searching
                          ? 'В поиске'
                          : lesson.substitute_teacher_name || 'Нет';

                        return (
                          <Table.Tr
                            key={lesson.id}
                            style={{ cursor: canEdit ? 'pointer' : 'default' }}
                            onClick={() => canEdit && setEditLesson(lesson)}
                          >
                            <Table.Td>
                              <Text size="sm" fw={500}>{lesson.group_name}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {formatTime(lesson.start_time)}–{formatTime(lesson.end_time)}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Badge size="xs" variant="filled" color={typeCfg.color} radius="xl">
                                {typeCfg.label}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Badge size="xs" variant="filled" color={statusCfg.color} radius="xl">
                                {statusCfg.label}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              {lesson.substitute_searching ? (
                                <Badge size="xs" variant="filled" color="red" radius="xl">
                                  В поиске
                                </Badge>
                              ) : lesson.substitute_teacher_name ? (
                                <Text size="sm">{lesson.substitute_teacher_name}</Text>
                              ) : (
                                <Text size="sm" c="dimmed">Нет</Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{lesson.teacher_name || '—'}</Text>
                            </Table.Td>
                            <Table.Td>
                              {lesson.subject_name ? (
                                <Badge size="xs" variant="filled" color="blue" radius="xl">
                                  {lesson.subject_name}
                                </Badge>
                              ) : (
                                <Text size="sm" c="dimmed">—</Text>
                              )}
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Box>
              );
            })}

            {/* Если за всю неделю нет занятий */}
            {data.days.every((d) => d.lessons.length === 0) && (
              <Text c="dimmed" ta="center" py="md">
                На этой неделе нет занятий
              </Text>
            )}
          </Stack>
        ) : (
          <Text c="dimmed" ta="center" py="md">
            Нет данных
          </Text>
        )}
      </BaseWidget>

      {/* Модалка редактирования занятия */}
      {editLesson && (
        <LessonEditModal
          opened={!!editLesson}
          onClose={() => setEditLesson(null)}
          lesson={editLesson}
          organizationId={organization?.id}
          onSaved={handleLessonSaved}
        />
      )}
    </>
  );
};

export default ScheduleWidget;
